import { api } from '../utils/apiClient';
import type {
  CreditWallet,
  CreditTransaction,
  PricingPlan,
  ActionPricing,
  CreditSystemConfig,
  CreditAnalytics,
} from '../types';

// ---------------------------------------------------------------------------
// User-facing endpoints
// ---------------------------------------------------------------------------

export const creditService = {
  getMyWallet: () => api.get<CreditWallet>('/api/credits/wallet'),

  getMyTransactions: (params?: {
    type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.type) qs.set('type', params.type);
    if (params?.start_date) qs.set('start_date', params.start_date);
    if (params?.end_date) qs.set('end_date', params.end_date);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const q = qs.toString();
    return api.get<{
      transactions: CreditTransaction[];
      total: number;
      page: number;
      page_size: number;
    }>(`/api/credits/transactions${q ? `?${q}` : ''}`);
  },

  getActionCosts: () =>
    api.get<{ actions: ActionPricing[] }>('/api/credits/action-costs'),

  getPlans: () =>
    api.get<{ plans: PricingPlan[] }>('/api/credits/plans'),

  getConfig: () =>
    api.get<CreditSystemConfig>('/api/credits/config'),
};

// ---------------------------------------------------------------------------
// Admin endpoints
// ---------------------------------------------------------------------------

export const adminCreditService = {
  // Wallets
  getWallets: (params?: { search?: string; page?: number; page_size?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const q = qs.toString();
    return api.get<{
      wallets: CreditWallet[];
      total: number;
      page: number;
      page_size: number;
    }>(`/api/admin/credits/wallets${q ? `?${q}` : ''}`);
  },

  getUserWallet: (userId: string) =>
    api.get<CreditWallet>(`/api/admin/credits/wallets/${userId}`),

  topupUser: (userId: string, data: { amount: number; description?: string; price_usd?: number }) =>
    api.post<{ message: string; new_balance: number }>(`/api/admin/credits/wallets/${userId}/topup`, data),

  assignPlan: (userId: string, data: { plan_id: string; allocate_credits?: boolean }) =>
    api.post<{ message: string; plan_name: string; credits_allocated?: number; new_balance?: number }>(
      `/api/admin/credits/wallets/${userId}/assign-plan`,
      data,
    ),

  // Transactions
  getTransactions: (params?: {
    user_id?: string;
    type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.user_id) qs.set('user_id', params.user_id);
    if (params?.type) qs.set('type', params.type);
    if (params?.start_date) qs.set('start_date', params.start_date);
    if (params?.end_date) qs.set('end_date', params.end_date);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const q = qs.toString();
    return api.get<{
      transactions: CreditTransaction[];
      total: number;
      page: number;
      page_size: number;
    }>(`/api/admin/credits/transactions${q ? `?${q}` : ''}`);
  },

  // Plans
  getPlans: (includeInactive = false) =>
    api.get<{ plans: PricingPlan[] }>(
      `/api/admin/credits/plans?include_inactive=${includeInactive}`,
    ),

  getPlan: (planId: string) =>
    api.get<PricingPlan>(`/api/admin/credits/plans/${planId}`),

  createPlan: (data: Omit<PricingPlan, 'id' | 'created_at' | 'updated_at' | 'subscriber_count'>) =>
    api.post<{ message: string; plan: PricingPlan }>('/api/admin/credits/plans', data),

  updatePlan: (planId: string, data: Partial<PricingPlan>) =>
    api.put<{ message: string; plan: PricingPlan }>(`/api/admin/credits/plans/${planId}`, data),

  deletePlan: (planId: string) =>
    api.delete<{ message: string }>(`/api/admin/credits/plans/${planId}`),

  // Per-plan action overrides
  getPlanOverrides: (planId: string) =>
    api.get<{ plan_id: string; action_overrides: Record<string, number> }>(
      `/api/admin/credits/plans/${planId}/overrides`,
    ),

  setPlanOverride: (planId: string, actionKey: string, credits: number | null) =>
    api.put<{
      message: string;
      plan_id: string;
      action_overrides: Record<string, number>;
    }>(
      `/api/admin/credits/plans/${planId}/overrides/${actionKey}`,
      { credits },
    ),

  deletePlanOverride: (planId: string, actionKey: string) =>
    api.delete<{
      message: string;
      plan_id: string;
      action_overrides: Record<string, number>;
    }>(`/api/admin/credits/plans/${planId}/overrides/${actionKey}`),

  // Action pricing
  getActionPricing: () =>
    api.get<{ actions: ActionPricing[] }>('/api/admin/credits/action-pricing'),

  updateActionPricing: (actionKey: string, data: { default_credits: number }) =>
    api.put<{ message: string; action: ActionPricing }>(
      `/api/admin/credits/action-pricing/${actionKey}`,
      data,
    ),

  // Config
  getConfig: () =>
    api.get<CreditSystemConfig>('/api/admin/credits/config'),

  updateConfig: (data: Partial<CreditSystemConfig>) =>
    api.put<{ message: string; config: CreditSystemConfig }>('/api/admin/credits/config', data),

  // Analytics
  getAnalytics: () =>
    api.get<CreditAnalytics>('/api/admin/credits/analytics'),
};
